import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

const pad = (n) => String(n).padStart(2, "0");
export const isoLocal = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const todayISO = () => isoLocal(new Date());
export const isoPlusDays = (n) => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() + n);
  return isoLocal(d);
};

const MONTHS_FULL = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const WEEKDAYS = ["S", "M", "T", "W", "T", "F", "S"];
const MARKER = { block: "bg-[#d23b2f]", custom: "bg-[#FF4D00]", booking: "bg-[#111111]" };

// Reusable month-grid date picker honoring [minDate, maxDate]. markers = { iso: "block"|"custom"|"booking" }.
export const MonthCalendar = ({ value, onSelect, minDate, maxDate, markers = {}, testid = "month-calendar" }) => {
  const base = value || minDate;
  const initDate = base ? new Date(base + "T00:00:00") : new Date();
  const [view, setView] = useState({ y: initDate.getFullYear(), m: initDate.getMonth() });

  const firstWeekday = new Date(view.y, view.m, 1).getDay();
  const daysInMonth = new Date(view.y, view.m + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(isoLocal(new Date(view.y, view.m, d)));

  const monthStart = isoLocal(new Date(view.y, view.m, 1));
  const monthEnd = isoLocal(new Date(view.y, view.m, daysInMonth));
  const prevDisabled = minDate && monthStart <= minDate;
  const nextDisabled = maxDate && monthEnd >= maxDate;

  const go = (delta) => setView((v) => {
    const d = new Date(v.y, v.m + delta, 1);
    return { y: d.getFullYear(), m: d.getMonth() };
  });

  return (
    <div data-testid={testid} className="rounded-[16px] border border-[#e9e6e1] bg-white p-4">
      <div className="mb-3 flex items-center justify-between">
        <button type="button" data-testid="cal-prev" disabled={prevDisabled} onClick={() => go(-1)}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[#111] transition-colors enabled:hover:bg-black/5 disabled:text-[#d8d4ce]">
          <ChevronLeft size={20} />
        </button>
        <p className="text-[15px] font-bold text-[#111]" data-testid="cal-month-label">{MONTHS_FULL[view.m]} {view.y}</p>
        <button type="button" data-testid="cal-next" disabled={nextDisabled} onClick={() => go(1)}
          className="flex h-9 w-9 items-center justify-center rounded-full text-[#111] transition-colors enabled:hover:bg-black/5 disabled:text-[#d8d4ce]">
          <ChevronRight size={20} />
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {WEEKDAYS.map((w, i) => (
          <div key={i} className="py-1 text-center text-[11px] font-semibold uppercase text-[#aaa]">{w}</div>
        ))}
        {cells.map((iso, i) => {
          if (!iso) return <div key={`e${i}`} />;
          const disabled = (minDate && iso < minDate) || (maxDate && iso > maxDate);
          const selected = value === iso;
          const mk = markers[iso];
          return (
            <button key={iso} type="button" data-testid={`cal-day-${iso}`} disabled={disabled}
              onClick={() => onSelect && onSelect(iso)}
              className={`relative flex h-10 items-center justify-center rounded-[10px] text-[14px] font-medium transition-colors ${
                disabled ? "cursor-not-allowed text-[#d8d4ce]"
                  : selected ? "bg-[#111111] text-white"
                  : "text-[#111111] hover:bg-[#f2f0ec]"
              }`}>
              {Number(iso.slice(8, 10))}
              {mk && !selected && <span className={`absolute bottom-1 h-1 w-1 rounded-full ${MARKER[mk] || "bg-[#111]"}`} />}
            </button>
          );
        })}
      </div>
    </div>
  );
};
